import { humanize, titleize, underscore } from "inflected";
import { keyFromApiIdentifier, nameToApiIdentifier } from "../../ApiIdentified";

export const ParentRelationshipKey = "__identified-by-parent-relationship__";

const shopifyModelKey = (modelName: string) => {
  const modelKey = modelName.replaceAll(" ", "");
  return `DataModel-Shopify-${modelKey}`;
};

export const isShopifyModelKey = (modelKey: string) => {
  return modelKey.startsWith("DataModel-Shopify-");
};

export const shopifyModelNameFromKey = (modelKey: string) => {
  if (modelKey === ShopifySellingPlanGroupProductVariantKey) {
    // key is shortened for this model because of key length limit
    return "Selling Plan Group Product Variant";
  } else if (modelKey === ShopifySellingPlanGroupProductKey) {
    return "Selling Plan Group Product";
  } else {
    return titleize(modelKey.replace("DataModel-Shopify-", ""));
  }
};

export const shopifyApiIdentifierFromKey = (modelKey: string) => {
  return nameToApiIdentifier(`Shopify ${shopifyModelNameFromKey(modelKey)}`);
};

export const shopifyModelKeyFromApiIdentifier = (apiIdentifier: string) => {
  return `DataModel-${keyFromApiIdentifier(apiIdentifier)}`;
};

const shopifyFieldKey = (modelName: string, fieldName: string) => {
  const modelKey = modelName.replaceAll(" ", "");
  const fieldKey = fieldName.replaceAll(" ", "");
  return `ModelField-Shopify-${modelKey}-${fieldKey}`;
};

export const prefixlessModelKey = (modelKey: string) => {
  return modelKey.replace(/^DataModel-/, "");
};

export const shopifyApiIdentifierToHumanizedName = (apiIdentifier: string) => {
  // eg "shopifyOrderTransaction to Shopify Order Transaction"
  return titleize(humanize(underscore(apiIdentifier)));
};

// Model keys
export const ShopifyAppKey = shopifyModelKey("App");
export const ShopifyAppCreditKey = shopifyModelKey("App Credit");
export const ShopifyAppInstallationKey = shopifyModelKey("App Installation");
export const ShopifyAppPurchaseOneTimeKey = shopifyModelKey("App Purchase One Time");
export const ShopifyAppSubscriptionKey = shopifyModelKey("App Subscription");
export const ShopifyAppUsageRecordKey = shopifyModelKey("App Usage Record");
export const ShopifyArticleKey = shopifyModelKey("Article");
export const ShopifyAssetKey = shopifyModelKey("Asset");
export const ShopifyBalanceTransactionKey = shopifyModelKey("Balance Transaction");
export const ShopifyBillingAddressKey = shopifyModelKey("Billing Address");
export const ShopifyBlogKey = shopifyModelKey("Blog");
export const ShopifyBulkOperationKey = shopifyModelKey("Bulk Operation");
export const ShopifyCarrierServiceKey = shopifyModelKey("CarrierService");
export const ShopifyCartKey = shopifyModelKey("Cart");
export const ShopifyCartLineItemKey = shopifyModelKey("Cart Line Item");
export const ShopifyCartTransformKey = shopifyModelKey("Cart Transform");
export const ShopifyCheckoutAppliedGiftCardKey = shopifyModelKey("Checkout Applied Gift Card");
export const ShopifyCheckoutKey = shopifyModelKey("Checkout");
export const ShopifyCheckoutLineItemKey = shopifyModelKey("Checkout Line Item");
export const ShopifyCheckoutShippingRateKey = shopifyModelKey("Checkout Shipping Rate");
export const ShopifyCollectionKey = shopifyModelKey("Collection");
export const ShopifyCollectKey = shopifyModelKey("Collect");
export const ShopifyCommentKey = shopifyModelKey("Comment");
export const ShopifyCompanyKey = shopifyModelKey("Company");
export const ShopifyCompanyLocationKey = shopifyModelKey("Company Location");
export const ShopifyCompanyAddressKey = shopifyModelKey("Company Address");
export const ShopifyCompanyContactKey = shopifyModelKey("Company Contact");
export const ShopifyCompanyContactRoleAssignmentKey = shopifyModelKey("Company Contact Role Assignment");
export const ShopifyCompanyContactRoleKey = shopifyModelKey("Company Contact Role");
export const ShopifyCountryKey = shopifyModelKey("Country");
export const ShopifyCustomerAddressKey = shopifyModelKey("Customer Address");
export const ShopifyCustomerKey = shopifyModelKey("Customer");
export const ShopifyCustomerMergeableKey = shopifyModelKey("Customer Mergeable");
export const ShopifyCustomerPaymentMethodKey = shopifyModelKey("Customer Payment Method");
export const ShopifyDiscountCodeKey = shopifyModelKey("Discount Code");
export const ShopifyDiscountKey = shopifyModelKey("Discount");
export const ShopifyDiscountRedeemCodeKey = shopifyModelKey("Discount Redeem Code");
export const ShopifyDiscountCustomerGetsProductKey = shopifyModelKey("Discount Customer Gets Product");
export const ShopifyDiscountCustomerBuysProductKey = shopifyModelKey("Discount Customer Buys Product");
export const ShopifyDiscountCustomerBuysProductVariantKey = shopifyModelKey("Discount Customer Buys Product Variant");
export const ShopifyDiscountCustomerGetsProductVariantKey = shopifyModelKey("Discount Customer Gets Product Variant");
export const ShopifyDiscountCustomerGetsCollectionKey = shopifyModelKey("Discount Customer Gets Collection");
export const ShopifyDiscountCustomerBuysCollectionKey = shopifyModelKey("Discount Customer Buys Collection");
export const ShopifyDisputeKey = shopifyModelKey("Dispute");
export const ShopifyDisputeEvidenceKey = shopifyModelKey("Dispute Evidence");
export const ShopifyDisputeEvidenceFulfillmentKey = shopifyModelKey("Dispute Evidence Fulfillment");
export const ShopifyDisputeFileUploadKey = shopifyModelKey("Dispute File Upload");
export const ShopifyDomainKey = shopifyModelKey("Domain");
export const ShopifyDraftOrderKey = shopifyModelKey("Draft Order");
export const ShopifyDraftOrderLineItemKey = shopifyModelKey("Draft Order Line Item");
export const ShopifyDraftOrderPlatformDiscountKey = shopifyModelKey("Draft Order Platform Discount");
export const ShopifyDraftOrderPlatformDiscountAllocationKey = shopifyModelKey("Draft Order Platform Discount Allocation");
export const ShopifyDutyKey = shopifyModelKey("Duty");
export const ShopifyEventKey = shopifyModelKey("Event");
export const ShopifyFileKey = shopifyModelKey("File");
export const ShopifyFulfillmentEventKey = shopifyModelKey("Fulfillment Event");
export const ShopifyFulfillmentKey = shopifyModelKey("Fulfillment");
export const ShopifyFulfillmentLineItemKey = shopifyModelKey("Fulfillment Line Item");
export const ShopifyFulfillmentOrderKey = shopifyModelKey("Fulfillment Order");
export const ShopifyFulfillmentOrderLineItemKey = shopifyModelKey("Fulfillment Order Line Item");
export const ShopifyFulfillmentServiceKey = shopifyModelKey("Fulfillment Service");
export const ShopifyGdprRequestKey = shopifyModelKey("Gdpr Request");
export const ShopifyGiftCardKey = shopifyModelKey("Gift Card");
export const ShopifyInventoryItemKey = shopifyModelKey("Inventory Item");
export const ShopifyInventoryLevelKey = shopifyModelKey("Inventory Level");
export const ShopifyLocationKey = shopifyModelKey("Location");
export const ShopifyMarketKey = shopifyModelKey("Market");
export const ShopifyMarketRegionKey = shopifyModelKey("Market Region");
export const ShopifyMarketWebPresenceKey = shopifyModelKey("Market Web Presence");
export const ShopifyOrderAdjustmentKey = shopifyModelKey("Order Adjustment");
export const ShopifyOrderKey = shopifyModelKey("Order");
export const ShopifyOrderLineItemKey = shopifyModelKey("Order Line Item");
export const ShopifyOrderRiskKey = shopifyModelKey("Order Risk");
export const ShopifyOrderTransactionKey = shopifyModelKey("Order Transaction");
export const ShopifyPageKey = shopifyModelKey("Page");
export const ShopifyPayoutKey = shopifyModelKey("Payout");
export const ShopifyPriceRuleKey = shopifyModelKey("Price Rule");
export const ShopifyProductImageKey = shopifyModelKey("Product Image");
export const ShopifyProductKey = shopifyModelKey("Product");
export const ShopifyProductMediaKey = shopifyModelKey("Product Media");
export const ShopifyProductOptionKey = shopifyModelKey("Product Option");
export const ShopifyProductVariantKey = shopifyModelKey("Product Variant");
export const ShopifyProductVariantMediaKey = shopifyModelKey("Product Variant Media");
export const ShopifyProvinceKey = shopifyModelKey("Province");
export const ShopifyRedirectKey = shopifyModelKey("Redirect");
export const ShopifyRefundDutyKey = shopifyModelKey("Refund Duty");
export const ShopifyRefundKey = shopifyModelKey("Refund");
export const ShopifyRefundLineItemKey = shopifyModelKey("Refund Line Item");
export const ShopifyScriptTagKey = shopifyModelKey("Script Tag");
export const ShopifySellingPlanGroupProductVariantKey = shopifyModelKey("Selling Plan Group Product Variant"); // model key length limit issue :(
export const ShopifySellingPlanGroupProductKey = shopifyModelKey("Selling Plan Group Applies To Product");
export const ShopifySellingPlanGroupKey = shopifyModelKey("Selling Plan Group");
export const ShopifySellingPlanKey = shopifyModelKey("Selling Plan");
export const ShopifyShippingAddressKey = shopifyModelKey("Shipping Address");
export const ShopifyShippingLineKey = shopifyModelKey("Shipping Line");
export const ShopifyShopKey = shopifyModelKey("Shop");
export const ShopifySubscriptionBillingAttemptKey = shopifyModelKey("Subscription Billing Attempt");
export const ShopifySubscriptionContractKey = shopifyModelKey("Subscription Contract");
export const ShopifySubscriptionLineKey = shopifyModelKey("Subscription Line");
export const ShopifySubscriptionManualDiscountKey = shopifyModelKey("Subscription Manual Discount");
export const ShopifySyncKey = shopifyModelKey("Sync");
export const ShopifyTenderTransactionKey = shopifyModelKey("Tender Transaction");
export const ShopifyThemeKey = shopifyModelKey("Theme");
export const ShopifyPaymentsAccountKey = shopifyModelKey("Payments Account");
export const ShopifyBusinessEntityKey = shopifyModelKey("Business Entity");

// Field keys
export const ShopifyAppToShopKey = shopifyFieldKey("App", "Shop");
export const ShopifyAppInstallationToAppKey = shopifyFieldKey("App Installation", "App");
export const ShopifyAppInstallationToShopKey = shopifyFieldKey("App Installation", "Shop");
export const ShopifyAppSubscriptionToAppInstallationKey = shopifyFieldKey("App Subscription", "App Installation");
export const ShopifySubscriptionContractToAppKey = shopifyFieldKey("Subscription Contract", "App");
export const ShopifyAppUsageRecordToAppSubscriptionKey = shopifyFieldKey("App Usage Record", "App Subscription");
export const ShopifyArticleToBlogKey = shopifyFieldKey("Article", "Blog");
export const ShopifyAssetToThemeKey = shopifyFieldKey("Asset", "Theme");
export const ShopifyBalanceTransactionToOrderKey = shopifyFieldKey("Balance Transaction", "Order");
export const ShopifyBalanceTransactionToOrderTransactionKey = shopifyFieldKey("Balance Transaction", "Order Transaction");
export const ShopifyBalanceTransactionToPayoutKey = shopifyFieldKey("Balance Transaction", "Payout");
export const ShopifyCartLineItemToCartKey = shopifyFieldKey("Cart Line Item", "Cart");
export const ShopifyCartLineItemToProductKey = shopifyFieldKey("Cart Line Item", "Product");
export const ShopifyCartLineItemToProductVariantKey = shopifyFieldKey("Cart Line Item", "Product Variant");
export const ShopifyCartLineItemShopifyIdKey = shopifyFieldKey("Cart Line Item", "Shopify Id");
export const ShopifyCartLineItemKeyUniquenessKey = "Validation-DataModel-ShopifyCartLineItem-Uniqueness";
export const ShopifyCheckoutLineItemToCheckoutKey = shopifyFieldKey("Checkout Line Item", "Checkout");
export const ShopifyCheckoutLineItemToProductKey = shopifyFieldKey("Checkout Line Item", "Product");
export const ShopifyCheckoutLineItemToVariantKey = shopifyFieldKey("Checkout Line Item", "Product Variant");
export const ShopifyCheckoutAppliedGiftCardToCheckoutKey = shopifyFieldKey("Gift Card", "Checkout");
export const ShopifyCheckoutShippingRateToCheckoutKey = shopifyFieldKey("Checkout Shipping Rate", "Checkout");
export const ShopifyCheckoutToBillingAddressKey = shopifyFieldKey("Checkout", "Billing Address");
export const ShopifyCheckoutToCustomerKey = shopifyFieldKey("Checkout", "Customer");
export const ShopifyCheckoutToLocationKey = shopifyFieldKey("Checkout", "Location");
export const ShopifyCheckoutToOrderKey = shopifyFieldKey("Checkout Order", "Order");
export const ShopifyCheckoutToShippingAddressKey = shopifyFieldKey("Checkout", "Shipping Address");
export const ShopifyCollectionToProductsKey = shopifyFieldKey("Collection", "Products");
export const ShopifyCollectToCollectionKey = shopifyFieldKey("Collect", "Collection");
export const ShopifyCollectToProductKey = shopifyFieldKey("Collect", "Product");
export const ShopifyCommentToArticleKey = shopifyFieldKey("Comment", "Article");
export const ShopifyCommentToBlogKey = shopifyFieldKey("Comment", "Blog");
export const ShopifyCustomerMergeableToCustomerKey = shopifyFieldKey("Customer Mergeable", "Customer");
export const ShopifyCustomerAddressToCustomerKey = shopifyFieldKey("Customer Address", "Customer");
export const ShopifyCustomerPaymentMethodToCustomerKey = shopifyFieldKey("Customer Payment Method", "Customer");
export const ShopifyCustomerPaymentMethodToShopKey = shopifyFieldKey("Customer Payment Method", "Shop");
export const ShopifyCustomerToOrderKey = shopifyFieldKey("Customer", "Order");
export const ShopifyDiscountAllocationToCheckoutLineItemKey = shopifyFieldKey("Discount Allocation", "Checkout Line Item");
export const ShopifyDiscountCodeToPriceRuleKey = shopifyFieldKey("Discount Code", "Price Rule");
export const ShopifyDiscountToDiscountRedeemCodesKey = shopifyFieldKey("Discount", "Discount Redeem Codes");
export const ShopifyDiscountToCustomerGetsCollectionKey = shopifyFieldKey("Discount", "Customer Gets Collection");
export const ShopifyDiscountToCustomerBuysCollectionKey = shopifyFieldKey("Discount", "Customer Buys Collection");
export const ShopifyDiscountToCustomerGetsProductKey = shopifyFieldKey("Discount", "Customer Gets Product");
export const ShopifyDiscountToCustomerBuysProductKey = shopifyFieldKey("Discount", "Customer Buys Product");
export const ShopifyDiscountToCustomerGetsProductVariantKey = shopifyFieldKey("Discount", "Customer Gets Product Variant");
export const ShopifyDiscountToCustomerBuysProductVariantKey = shopifyFieldKey("Discount", "Customer Buys Product Variant");
export const ShopifyDiscountRedeemCodeToDiscountKey = shopifyFieldKey("Discount Redeem Code", "Discount");
export const ShopifyDiscountCustomerGetsProductToDiscountKey = shopifyFieldKey("Discount Customer Gets Product", "Discount");
export const ShopifyDiscountCustomerBuysProductToDiscountKey = shopifyFieldKey("Discount Customer Buys Product", "Discount");
export const ShopifyDiscountCustomerGetsCollectionToDiscountKey = shopifyFieldKey("Discount Customer Gets Collection", "Discount");
export const ShopifyDiscountCustomerBuysCollectionToDiscountKey = shopifyFieldKey("Discount Customer Buys Collection", "Discount");
export const ShopifyDiscountCustomerGetsProductVariantToDiscountKey = shopifyFieldKey("Discount Customer Gets Product Variant", "Discount");
export const ShopifyDiscountCustomerBuysProductVariantToDiscountKey = shopifyFieldKey("Discount Customer Buys Product Variant", "Discount");
export const ShopifyDiscountCustomerGetsCollectionToShopKey = shopifyFieldKey("Discount Customer Gets Collection", "Shop");
export const ShopifyDiscountCustomerBuysCollectionToShopKey = shopifyFieldKey("Discount Customer Buys Collection", "Shop");
export const ShopifyDiscountCustomerGetsProductToShopKey = shopifyFieldKey("Discount Customer Gets Product", "Shop");
export const ShopifyDiscountCustomerBuysProductToShopKey = shopifyFieldKey("Discount Customer Buys Product", "Shop");
export const ShopifyDiscountCustomerGetsProductVariantToShopKey = shopifyFieldKey("Discount Customer Gets Product Variant", "Shop");
export const ShopifyDiscountCustomerBuysProductVariantToShopKey = shopifyFieldKey("Discount Customer Buys Product Variant", "Shop");
export const ShopifyDiscountCustomerGetsCollectionToCollectionKey = shopifyFieldKey("Discount Customer Gets Collection", "Collection");
export const ShopifyDiscountCustomerBuysCollectionToCollectionKey = shopifyFieldKey("Discount Customer Buys Collection", "Collection");
export const ShopifyDiscountCustomerGetsProductToProductKey = shopifyFieldKey("Discount Customer Gets Product", "Product");
export const ShopifyDiscountCustomerBuysProductToProductKey = shopifyFieldKey("Discount Customer Buys Product", "Product");
export const ShopifyDiscountCustomerGetsProductVariantToProductVariantKey = shopifyFieldKey(
  "Discount Customer Gets Product Variant",
  "Product Variant"
);
export const ShopifyDiscountCustomerBuysProductVariantToProductVariantKey = shopifyFieldKey(
  "Discount Customer Buys Product Variant",
  "Product Variant"
);
export const ShopifyDisputeToOrderKey = shopifyFieldKey("Dispute", "Order");
export const ShopifyDisputeToEvidenceKey = shopifyFieldKey("Dispute", "Dispute Evidence");
export const ShopifyDisputeEvidenceToDisputeKey = shopifyFieldKey("Dispute Evidence", "Dispute");
export const ShopifyDisputeEvidenceCancellationPolicyFileKey = shopifyFieldKey("Dispute Evidence", "Cancellation Policy File");
export const ShopifyDisputeEvidenceCustomerCommunicationFileKey = shopifyFieldKey("Dispute Evidence", "Customer Communication File");
export const ShopifyDisputeEvidenceCustomerSignatureFileKey = shopifyFieldKey("Dispute Evidence", "Customer Signature File");
export const ShopifyDisputeEvidenceRefundPolicyFileKey = shopifyFieldKey("Dispute Evidence", "Refund Policy File");
export const ShopifyDisputeEvidenceServiceDocumentationFileKey = shopifyFieldKey("Dispute Evidence", "Service Documentation File");
export const ShopifyDisputeEvidenceShippingDocumentationFileKey = shopifyFieldKey("Dispute Evidence", "Shipping Documentation File");
export const ShopifyDisputeEvidenceUncategorizedFileKey = shopifyFieldKey("Dispute Evidence", "Uncategorized File");
export const ShopifyDisputeEvidenceFulfillmentToDisputeEvidenceKey = shopifyFieldKey("Dispute Evidence Fulfillment", "Dispute Evidence");
export const ShopifyDisputeFileUploadToDisputeEvidenceKey = shopifyFieldKey("Dispute File Upload", "Dispute Evidence");
export const ShopifyDraftOrderToDraftOrderPlatformDiscountKey = shopifyFieldKey("Draft Order", "Draft Order Platform Discount");
export const ShopifyDraftOrderLineItemToDraftOrderKey = shopifyFieldKey("Draft Order Line Item", "Draft Order");
export const ShopifyDraftOrderLineItemToFulfillmentServiceKey = shopifyFieldKey("Draft Order Line Item", "Fulfillment Service");
export const ShopifyDraftOrderLineItemToProductKey = shopifyFieldKey("Draft Order Line Item", "Product");
export const ShopifyDraftOrderLineItemToProductImageKey = shopifyFieldKey("Draft Order Line Item", "Product Image");
export const ShopifyDraftOrderLineItemToProductVariantKey = shopifyFieldKey("Draft Order Line Item", "Product Variant");
export const ShopifyDraftOrderPlatformDiscountToDraftOrderPlatformDiscountAllocationKey = shopifyFieldKey(
  "Draft Order Platform Discount",
  "Draft Order Platform Discount Allocation"
);
export const ShopifyDraftOrderToCustomerKey = shopifyFieldKey("Draft Order", "Customer");
export const ShopifyDutyToOrderLineItemKey = shopifyFieldKey("Duty", "Order Line Item");
export const ShopifyFileToProductKey = shopifyFieldKey("File", "Product");
export const ShopifyFileToProductVariantKey = shopifyFieldKey("File", "Product Variant");
export const ShopifyFulfillmentEventToFulfillmentKey = shopifyFieldKey("Fulfillment Event", "Fulfillment");
export const ShopifyFulfillmentEventToOrderKey = shopifyFieldKey("Fulfillment Event", "Order");
export const ShopifyFulfillmentLineItemToFulfillmentKey = shopifyFieldKey("Fulfillment Line Item", "Fulfillment");
export const ShopifyFulfillmentLineItemToOrderLineItemKey = shopifyFieldKey("Fulfillment Line Item", "Order Line Item");
export const ShopifyFulfillmentOrderLineItemToFulfillmentOrderKey = shopifyFieldKey("Fulfillment Order Line Item", "Fulfillment Order");
export const ShopifyFulfillmentOrderLineItemToShopifyInventoryItemKey = shopifyFieldKey("Fulfillment Order Line Item", "Inventory Item");
export const ShopifyFulfillmentOrderLineItemToProductVariantKey = shopifyFieldKey("Fulfillment Order Line Item", "Product Variant");
export const ShopifyFulfillmentOrderLineItemToOrderLineItemKey = shopifyFieldKey("Fulfillment Order Line Item", "Order Line Item");
export const ShopifyFulfillmentOrderToFulfillmentOrderLineItemsKey = shopifyFieldKey("Fulfillment Order", "Fulfillment Order Line Items");
export const ShopifyFulfillmentOrderToOrderKey = shopifyFieldKey("Fulfillment Order", "Order");
export const ShopifyFulfillmentServiceToLocationKey = shopifyFieldKey("Fulfillment Service", "Location");
export const ShopifyFulfillmentToFulfillmentEventsKey = shopifyFieldKey("Fulfillment", "Fulfillment Events");
export const ShopifyFulfillmentToFulfillmentLineItemsKey = shopifyFieldKey("Fulfillment", "Fulfillment Line Items");
export const ShopifyFulfillmentToLocationKey = shopifyFieldKey("Fulfillment", "Location");
export const ShopifyFulfillmentToOrderKey = shopifyFieldKey("Fulfillment", "Order");
export const ShopifyGiftCardToOrderKey = shopifyFieldKey("Gift Card", "Order");
export const ShopifyGiftCardToCustomerKey = shopifyFieldKey("Gift Card", "Customer");
export const ShopifyInventoryItemToLocationsKey = shopifyFieldKey("Inventory Item", "Locations");
export const ShopifyInventoryLevelToInventoryItemKey = shopifyFieldKey("Inventory Level", "Inventory Item");
export const ShopifyInventoryLevelToLocationKey = shopifyFieldKey("Inventory Level", "Location");
export const ShopifyLocationToInventoryItemsKey = shopifyFieldKey("Inventory Location", "Inventory Items");
export const ShopifyMarketRegionToMarketKey = shopifyFieldKey("Market Region", "Market");
export const ShopifyMarketWebPresenceToMarketKey = shopifyFieldKey("Market Web Presence", "Market");
export const ShopifyMarketWebPresenceToDomainKey = shopifyFieldKey("Market Web Presence", "Domain");
// We migrate the webPresence field on Market from a HasOne to a BelongsTo but we need the key assigned
// to the BelongsTo field to have the value that the old HasOne field had; this is the key we use for that
export const ShopifyMarketPrimaryWebPresenceToMarketKey = "ModelField-DataModel-Shopify-Market-webPresence";
export const ShopifyOrderAdjustmentToOrderKey = shopifyFieldKey("Order Adjustment", "Order");
export const ShopifyOrderAdjustmentToRefundKey = shopifyFieldKey("Order Adjustment", "Refund");
export const ShopifyOrderLineItemToOrderKey = shopifyFieldKey("Order Line Item", "Order");
export const ShopifyOrderLineItemToOriginLocationKey = shopifyFieldKey("Order Line Item", "Location");
export const ShopifyOrderLineItemToProductKey = shopifyFieldKey("Order Line Item", "Product");
export const ShopifyOrderLineItemToProductVariantKey = shopifyFieldKey("Order Line Item", "Product Variant");
export const ShopifyOrderRiskToOrderKey = shopifyFieldKey("Order Risk", "Order");
export const ShopifyOrderToCustomerKey = shopifyFieldKey("Order", "Customer");
export const ShopifyOrderToBusinessEntityKey = shopifyFieldKey("Order", "Business Entity");
export const ShopifyOrderToLocationKey = shopifyFieldKey("Order", "Location");
export const ShopifyOrderToOrderRisksKey = shopifyFieldKey("Order", "Order Risk");
export const ShopifyOrderTransactionToLocationKey = shopifyFieldKey("Order Transaction", "Location");
export const ShopifyOrderTransactionToOrderKey = shopifyFieldKey("Order Transaction", "Order");
export const ShopifyOrderTransactionToParentKey = shopifyFieldKey("Order Transaction", "Parent");
export const ShopifyOrderTransactionToRefundKey = shopifyFieldKey("Order Transaction", "Refund");
export const ShopifyOrderTransactionToUserKey = shopifyFieldKey("Order Transaction", "User");
export const ShopifyPriceRuleToDiscountCodesKey = shopifyFieldKey("Price Rule", "Discount Codes");
export const ShopifyProductImageToProductKey = shopifyFieldKey("Product Image", "Product");
export const ShopifyProductMediaToProductKey = shopifyFieldKey("Product Media", "Product");
export const ShopifyFeaturedProductMediaToProductKey = shopifyFieldKey("Featured Product Media", "Product");
export const ShopifyProductMediaToFileKey = shopifyFieldKey("Product Media", "File");
export const ShopifyProductOptionToProductKey = shopifyFieldKey("Product Option", "Product");
export const ShopifyProductToCollectionsKey = shopifyFieldKey("Product", "Collections");
export const ShopifyProductToCollectsKey = shopifyFieldKey("Product", "Collects");
export const ShopifyProductToFileKey = shopifyFieldKey("Product", "File");
export const ShopifyProductToSellingPlanGroupKey = shopifyFieldKey("Product", "Selling Plan Group");
export const ShopifyCollectionToCustomerGetsCollectionKey = shopifyFieldKey("Collection", "Customer Gets Collection");
export const ShopifyCollectionToCustomerBuysCollectionKey = shopifyFieldKey("Collection", "Customer Buys Collection");
export const ShopifyProductToCustomerGetsProductKey = shopifyFieldKey("Product", "Customer Gets Product");
export const ShopifyProductToCustomerBuysProductKey = shopifyFieldKey("Product", "Customer Buys Product");
export const ShopifyProductVariantToCustomerGetsProductVariantKey = shopifyFieldKey("Product Variant", "Customer Gets Product Variant");
export const ShopifyProductVariantToCustomerBuysProductVariantKey = shopifyFieldKey("Product Variant", "Customer Buys Product Variant");
export const ShopifyProductVariantToSellingPlanGroupKey = shopifyFieldKey("Product Variant", "Selling Plan Group");
export const ShopifyProductVariantToInventoryItemKey = shopifyFieldKey("Product Variant", "Inventory Item");
export const ShopifyProductVariantToProductImageKey = shopifyFieldKey("Product Variant", "Product Image");
export const ShopifyProductVariantToProductKey = shopifyFieldKey("Product Variant", "Product");
export const ShopifyProductVariantToFileKey = shopifyFieldKey("Product Variant", "File");
export const ShopifyProductVariantMediaToProductVariantKey = shopifyFieldKey("Product Variant Media", "Product Variant");
export const ShopifyProductVariantMediaToFileKey = shopifyFieldKey("Product Variant Media", "File");
export const ShopifyProvinceToCountryKey = shopifyFieldKey("Province", "Country");
export const ShopifyRefundDutyToDutyKey = shopifyFieldKey("Refund Duty", "Duty");
export const ShopifyRefundDutyToRefundKey = shopifyFieldKey("Refund Duty", "Refund");
export const ShopifyRefundLineItemToLocationKey = shopifyFieldKey("Refund Line Item", "Location");
export const ShopifyRefundLineItemToOrderLineItemKey = shopifyFieldKey("Refund Line Item", "Order");
export const ShopifyRefundLineItemToRefundKey = shopifyFieldKey("Refund Line Item", "Refund");
export const ShopifyRefundToOrderKey = shopifyFieldKey("Refund", "Order");
export const ShopifySellingPlanToSellingPlanGroupKey = shopifyFieldKey("Selling Plan", "Selling Plan Group");
export const ShopifySellingPlanGroupProductToSellingPlanGroupKey = shopifyFieldKey("Selling Plan Group Product", "Selling Plan Group");
export const ShopifySellingPlanGroupProductVariantToSellingPlanGroupKey = shopifyFieldKey(
  "Selling Plan Group Product Variant",
  "Selling Plan Group"
);
export const ShopifySellingPlanGroupProductToProductKey = shopifyFieldKey("Selling Plan Group Product", "Product");
export const ShopifySellingPlanGroupProductVariantToProductVariantKey = shopifyFieldKey(
  "Selling Plan Group Product Variant",
  "Product Variant"
);
export const ShopifySellingPlanGroupToProductKey = shopifyFieldKey("Selling Plan Group", "Product");
export const ShopifySellingPlanGroupToProductVariantKey = shopifyFieldKey("Selling Plan Group", "Product Variant");
export const ShopifyShippingLineToFulfillmentServiceKey = shopifyFieldKey("Shipping Line", "Fulfillment Service");
export const ShopifyShippingLineToOrderKey = shopifyFieldKey("Shipping Line", "Order");
export const ShopifyShopToPrimaryLocationKey = shopifyFieldKey("Shop", "Primary Location");
export const ShopifySubscriptionBillingAttemptToOrderKey = shopifyFieldKey("Subscription Billing Attempt", "Order");
export const ShopifySubscriptionBillingAttemptToSubscriptionContractKey = shopifyFieldKey(
  "Subscription Billing Attempt",
  "Subscription Contract"
);
export const ShopifySubscriptionContractToCustomerKey = shopifyFieldKey("Subscription Contract", "Customer");
export const ShopifySubscriptionContractToCustomerPaymentMethodKey = shopifyFieldKey("Subscription Contract", "Customer Payment Method");
export const ShopifySubscriptionLineToProductVariantKey = shopifyFieldKey("Subscription Line", "Product Variant");
export const ShopifySubscriptionLineToSellingPlanKey = shopifyFieldKey("Subscription Line", "Selling Plan");
export const ShopifySubscriptionLineToSubscriptionContractKey = shopifyFieldKey("Subscription Line", "Subscription Contract");
export const ShopifySubscriptionManualDiscountToSubscriptionContractKey = shopifyFieldKey(
  "Subscription Manual Discount",
  "Subscription Contract"
);
export const ShopifyTenderTransactionToOrderKey = shopifyFieldKey("Tender Transaction", "Order");

// Belongs to Shop field keys
export const ShopifyAppCreditToShopKey = shopifyFieldKey("App Credit", "Shop");
export const ShopifyAppPurchaseOneTimeToShopKey = shopifyFieldKey("App Purchase One Time", "Shop");
export const ShopifyAppSubscriptionToShopKey = shopifyFieldKey("App Subscription", "Shop");
export const ShopifyAppUsageRecordToShopKey = shopifyFieldKey("App Usage Record", "Shop");
export const ShopifyArticleToShopKey = shopifyFieldKey("Article", "Shop");
export const ShopifyAssetToShopKey = shopifyFieldKey("Asset", "Shop");
export const ShopifyBalanceTransactionToShopKey = shopifyFieldKey("Balance Transaction", "Shop");
export const ShopifyBillingAddressToShopKey = shopifyFieldKey("Billing Address", "Shop");
export const ShopifyBlogToShopKey = shopifyFieldKey("Blog", "Shop");
export const ShopifyBulkOperationToShopKey = shopifyFieldKey("Bulk Operation", "Shop");
export const ShopifyCarrierServiceToShopKey = shopifyFieldKey("CarrierService", "Shop");
export const ShopifyCartLineItemToShopKey = shopifyFieldKey("Cart Line Item", "Shop");
export const ShopifyCartToShopKey = shopifyFieldKey("Cart", "Shop");
export const ShopifyCheckoutAppliedGiftCardToShopKey = shopifyFieldKey("Checkout Applied Gift Card", "Shop");
export const ShopifyCheckoutLineItemToShopKey = shopifyFieldKey("Checkout Line Item", "Shop");
export const ShopifyCheckoutShippingRateToShopKey = shopifyFieldKey("Checkout Shipping Rate", "Shop");
export const ShopifyCheckoutToShopKey = shopifyFieldKey("Checkout", "Shop");
export const ShopifyCollectToShopKey = shopifyFieldKey("Collect", "Shop");
export const ShopifyCollectionToShopKey = shopifyFieldKey("Collection", "Shop");
export const ShopifyCommentToShopKey = shopifyFieldKey("Comment", "Shop");
export const ShopifyCompanyBillingAddressToCompanyLocationKey = shopifyFieldKey("Company Billing Address", "Company Location");
export const ShopifyCompanyShippingAddressToCompanyLocationKey = shopifyFieldKey("Company Shipping Address", "Company Location");
export const ShopifyCompanyContactDefaultRoleToCompanyKey = shopifyFieldKey("Company Contact Default Role", "Company");
export const ShopifyCompanyContactRoleAssignmentToCompanyContactKey = shopifyFieldKey("Company Contact Role Assignment", "Company Contact");
export const ShopifyCompanyContactRoleAssignmentToCompanyContactRoleKey = shopifyFieldKey("Company Contact Role", "Company Contact Role");
export const ShopifyCompanyContactRoleAssignmentToCompanyKey = shopifyFieldKey("Company Contact Role Assignment", "Company");
export const ShopifyCompanyContactRoleAssignmentToCompanyLocationKey = shopifyFieldKey(
  "Company Contact Role Assignment",
  "Company Location"
);
export const ShopifyCompanyContactRoleToCompanyKey = shopifyFieldKey("Company Contact Role", "Company");
export const ShopifyCompanyContactToCompanyKey = shopifyFieldKey("Company Contact", "Company");
export const ShopifyCompanyContactToCustomerKey = shopifyFieldKey("Company Contact", "Customer");
export const ShopifyCompanyLocationToCompanyKey = shopifyFieldKey("Company Location", "Company");
export const ShopifyCompanyLocationToMarketKey = shopifyFieldKey("Company Location", "Market");
export const ShopifyCompanyMainContactToCompanyKey = shopifyFieldKey("Company Main Contact", "Company");
export const ShopifyCompanyToCompanyContactKey = shopifyFieldKey("Company", "Company Contact");
export const ShopifyCompanyToCompanyContactRoleKey = shopifyFieldKey("Company", "Company Contact Role");
export const ShopifyCompanyToCompanyLocationKey = shopifyFieldKey("Company", "Company Location");
export const ShopifyCompanyToShopKey = shopifyFieldKey("Company", "Shop");
export const ShopifyCompanyContactToShopKey = shopifyFieldKey("Company Contact", "Shop");
export const ShopifyCompanyContactRoleToShopKey = shopifyFieldKey("Company Contact Role", "Shop");
export const ShopifyCompanyContactRoleAssignmentToShopKey = shopifyFieldKey("Company Contact Role Assignment", "Shop");
export const ShopifyCompanyAddressToShopKey = shopifyFieldKey("Company Address", "Shop");
export const ShopifyCompanyLocationToShopKey = shopifyFieldKey("Company Location", "Shop");
export const ShopifyCountryToShopKey = shopifyFieldKey("Country", "Shop");
export const ShopifyCustomerAddressToShopKey = shopifyFieldKey("Customer Address", "Shop");
export const ShopifyCustomerMergeableToShopKey = shopifyFieldKey("Customer Mergeable", "Shop");
export const ShopifyCustomerToShopKey = shopifyFieldKey("Customer", "Shop");
export const ShopifyDiscountAllocationToShopKey = shopifyFieldKey("Discount Allocation", "Shop");
export const ShopifyDiscountCodeToShopKey = shopifyFieldKey("Discount Code", "Shop");
export const ShopifyDiscountRedeemCodeToShopKey = shopifyFieldKey("Discount Redeem Code", "Shop");
export const ShopifyDiscountToShopKey = shopifyFieldKey("Discount", "Shop");
export const ShopifyDisputeEvidenceFulfillmentToShopKey = shopifyFieldKey("Dispute Evidence Fulfillment", "Shop");
export const ShopifyDisputeEvidenceToShopKey = shopifyFieldKey("Dispute Evidence", "Shop");
export const ShopifyDisputeFileUploadToShopKey = shopifyFieldKey("Dispute File Upload", "Shop");
export const ShopifyDisputeToShopKey = shopifyFieldKey("Dispute", "Shop");
export const ShopifyDomainToShopKey = shopifyFieldKey("Domain", "Shop");
export const ShopifyDraftOrderLineItemToShopKey = shopifyFieldKey("Draft Order Line Item", "Shop");
export const ShopifyDraftOrderToCompanyContactKey = shopifyFieldKey("Draft Order", "Company Contact");
export const ShopifyDraftOrderToCompanyKey = shopifyFieldKey("Draft Order", "Company");
export const ShopifyDraftOrderToCompanyLocationKey = shopifyFieldKey("Draft Order", "Company Location");
export const ShopifyDraftOrderToOrderKey = shopifyFieldKey("Draft Order", "Order");
export const ShopifyDraftOrderToShopKey = shopifyFieldKey("Draft Order", "Shop");
export const ShopifyDraftOrderPlatformDiscountToShopKey = shopifyFieldKey("Draft Order Platform Discount", "Shop");
export const ShopifyDraftOrderPlatformDiscountAllocationToShopKey = shopifyFieldKey("Draft Order Platform Discount Allocation", "Shop");
export const ShopifyDutyToShopKey = shopifyFieldKey("Duty", "Shop");
export const ShopifyEventToCompanyKey = shopifyFieldKey("Event", "Company");
export const ShopifyEventToCompanyLocationKey = shopifyFieldKey("Event", "Company Location");
export const ShopifyEventToShopKey = shopifyFieldKey("Event", "Shop");
export const ShopifyFileToShopKey = shopifyFieldKey("File", "Shop");
export const ShopifyFileOriginalSourceKey = shopifyFieldKey("File", "Original Source");
export const ShopifyFileSourcesKey = shopifyFieldKey("File", "Sources");
export const ShopifyFulfillmentEventToShopKey = shopifyFieldKey("Fulfillment Event", "Shop");
export const ShopifyFulfillmentLineItemToShopKey = shopifyFieldKey("Fulfillment Line Item", "Shop");
export const ShopifyFulfillmentOrderLineItemToShopKey = shopifyFieldKey("Fulfillment Order Line Item", "Shop");
export const ShopifyFulfillmentOrderToShopKey = shopifyFieldKey("Fulfillment Order", "Shop");
export const ShopifyFulfillmentOrderToLocationKey = shopifyFieldKey("Fulfillment Order", "Location");
export const ShopifyFulfillmentServiceToShopKey = shopifyFieldKey("Fulfillment Service", "Shop");
export const ShopifyFulfillmentToShopKey = shopifyFieldKey("Fulfillment", "Shop");
export const ShopifyGdprRequestToShopKey = shopifyFieldKey("Gdpr Request", "Shop");
export const ShopifyGiftCardToShopKey = shopifyFieldKey("Gift Card", "Shop");
export const ShopifyInventoryItemToShopKey = shopifyFieldKey("Inventory Item", "Shop");
export const ShopifyInventoryLevelToShopKey = shopifyFieldKey("Inventory Level", "Shop");
export const ShopifyLocationToCompanyKey = shopifyFieldKey("Location", "Company");
export const ShopifyLocationToShopKey = shopifyFieldKey("Location", "Shop");
export const ShopifyMarketRegionToShopKey = shopifyFieldKey("Market Region", "Shop");
export const ShopifyMarketToCompanyLocationKey = shopifyFieldKey("Market", "Company Location");
export const ShopifyMarketToShopKey = shopifyFieldKey("Market", "Shop");
export const ShopifyMarketWebPresenceToShopKey = shopifyFieldKey("Market Web Presence", "Shop");
export const ShopifyOrderAdjustmentToShopKey = shopifyFieldKey("Order Adjustment", "Shop");
export const ShopifyOrderLineItemToShopKey = shopifyFieldKey("Order Line Item", "Shop");
export const ShopifyOrderRiskToShopKey = shopifyFieldKey("Order Risk", "Shop");
export const ShopifyOrderToCompanyContactKey = shopifyFieldKey("Order", "Company Contact");
export const ShopifyOrderToCompanyKey = shopifyFieldKey("Order", "Company");
export const ShopifyOrderToCompanyLocationKey = shopifyFieldKey("Order", "Company Location");
export const ShopifyOrderToShopKey = shopifyFieldKey("Order", "Shop");
export const ShopifyOrderToSubscriptionBillingAttemptKey = shopifyFieldKey("Order", "Subscription Billing Attempts");
export const ShopifyOrderToSubscriptionContractKey = shopifyFieldKey("Order", "Subscription Contract");
export const ShopifyOrderTransactionToShopKey = shopifyFieldKey("Order Transaction", "Shop");
export const ShopifyPageToShopKey = shopifyFieldKey("Page", "Shop");
export const ShopifyPayoutToShopKey = shopifyFieldKey("Payout", "Shop");
export const ShopifyPriceRuleToShopKey = shopifyFieldKey("Price Rule", "Shop");
export const ShopifyProductImageToShopKey = shopifyFieldKey("Product Image", "Shop");
export const ShopifyProductMediaToShopKey = shopifyFieldKey("Product Media", "Shop");
export const ShopifyProductOptionToShopKey = shopifyFieldKey("Product Option", "Shop");
export const ShopifyProductToShopKey = shopifyFieldKey("Product", "Shop");
export const ShopifyProductVariantToShopKey = shopifyFieldKey("Product Variant", "Shop");
export const ShopifyProductVariantMediaToShopKey = shopifyFieldKey("Product Variant Media", "Shop");
export const ShopifyProvinceToShopKey = shopifyFieldKey("Province", "Shop");
export const ShopifyRedirectToShopKey = shopifyFieldKey("Redirect", "Shop");
export const ShopifyRefundDutyToShopKey = shopifyFieldKey("Refund Duty", "Shop");
export const ShopifyRefundLineItemToShopKey = shopifyFieldKey("Refund Line Item", "Shop");
export const ShopifyRefundToShopKey = shopifyFieldKey("Refund", "Shop");
export const ShopifyScriptTagToShopKey = shopifyFieldKey("Script Tag", "Shop");
export const ShopifySellingPlanGroupProductToShopKey = shopifyFieldKey("Selling Plan Group Product", "Shop");
export const ShopifySellingPlanGroupProductVariantToShopKey = shopifyFieldKey("Selling Plan Group Product Variant", "Shop");
export const ShopifySellingPlanGroupToShopKey = shopifyFieldKey("Selling Plan Group", "Shop");
export const ShopifySellingPlanToShopKey = shopifyFieldKey("Selling Plan", "Shop");
export const ShopifyShippingAddressToShopKey = shopifyFieldKey("Shipping Address", "Shop");
export const ShopifyShippingLineToShopKey = shopifyFieldKey("Shipping Line", "Shop");
export const ShopifySubscriptionBillingAttemptToShopKey = shopifyFieldKey("Billing Attempt", "Shop");
export const ShopifySubscriptionContractToShopKey = shopifyFieldKey("Subscription Contract", "Shop");
export const ShopifySubscriptionLineToShopKey = shopifyFieldKey("Subscription Line", "Shop");
export const ShopifySubscriptionManualDiscountToShopKey = shopifyFieldKey("Subscription Manual Discount", "Shop");
export const ShopifySyncForceKey = shopifyFieldKey("Sync", "Force");
export const ShopifySyncModelsKey = shopifyFieldKey("Sync", "Models");
export const ShopifySyncToShopKey = shopifyFieldKey("Sync", "Shop");
export const ShopifySyncErrorMessageKey = shopifyFieldKey("Sync", "Error Message");
export const ShopifySyncErrorDetailsKey = shopifyFieldKey("Sync", "Error Details");
export const ShopifySyncDomainKey = shopifyFieldKey("Sync", "Domain");
export const ShopifyTenderTransactionToShopKey = shopifyFieldKey("Tender Transaction", "Shop");
export const ShopifyThemeToShopKey = shopifyFieldKey("Theme", "Shop");
export const ShopifyBusinessEntityToShopifyPaymentsAccountKey = shopifyFieldKey("Business Entity", "Shopify Payments Account");
export const ShopifyPaymentsAccountToShopKey = shopifyFieldKey("Shopify Payments Account", "Shop");
export const ShopifyBusinessEntityToShopKey = shopifyFieldKey("Business Entity", "Shop");

// Shopify Action Keys
export const ShopifyShopInstallKey = "Action-Shopify-Shop-Create";
export const ShopifyShopInstallApiIdentifier = "install";
export const ShopifyShopInstallLegacyApiIdentifier = "create"; // Was at one time the `create` action, but is now `install`
export const ShopifyShopUpdateKey = "Action-Shopify-Shop-Update";
export const ShopifyShopUpdateApiIdentifier = "update";
export const ShopifyShopReinstallKey = "Action-Shopify-Shop-Reinstall";
export const ShopifyShopReinstallApiIdentifier = "reinstall";
export const ShopifyShopUninstallKey = "Action-Shopify-Shop-Uninstall";
export const ShopifyShopUninstallApiIdentifier = "uninstall";

export const ShopifyShopActionKeyToApiIdentifiersMap = {
  [ShopifyShopInstallKey]: [ShopifyShopInstallApiIdentifier, ShopifyShopInstallLegacyApiIdentifier],
  [ShopifyShopUpdateKey]: [ShopifyShopUpdateApiIdentifier],
  [ShopifyShopReinstallKey]: [ShopifyShopReinstallApiIdentifier],
  [ShopifyShopUninstallKey]: [ShopifyShopUninstallApiIdentifier],
} as const;

export const ShopifySyncRunKey = `Action-Shopify-Sync-Run`;
export const ShopifySyncRunApiIdentifier = "run";
export const ShopifySyncCompleteKey = `Action-Shopify-Sync-Complete`;
export const ShopifySyncCompleteApiIdentifier = "complete";
export const ShopifySyncErrorKey = `Action-Shopify-Sync-Error`;
export const ShopifySyncErrorApiIdentifier = "error";
export const ShopifySyncAbortKey = `Action-Shopify-Sync-Abort`;
export const ShopifySyncAbortApiIdentifier = "abort";

const ShopifySyncActionKeyToApiIdentifierMap = {
  [ShopifySyncRunKey]: [ShopifySyncRunApiIdentifier],
  [ShopifySyncCompleteKey]: [ShopifySyncCompleteApiIdentifier],
  [ShopifySyncErrorKey]: [ShopifySyncErrorApiIdentifier],
  [ShopifySyncAbortKey]: [ShopifySyncAbortApiIdentifier],
} as const;

export const ShopifySyncStartKey = "State-Shopify-Sync-Start";
export const ShopifySyncRunningKey = "State-Shopify-Sync-Running";
export const ShopifySyncCompletedKey = "State-Shopify-Sync-Completed";
export const ShopifySyncErroredKey = "State-Shopify-Sync-Errored";
export const ShopifySyncGlobalActionKey = "Global-Action-Shopify-Sync";
export const ShopifyScheduledSyncActionKey = "Global-Action-Shopify-Scheduled-Sync";
export const ShopifyRequiredValidationKey = "Shopify-ModelField-Required-Validation";

export const ShopifyAppSubscriptionLineItemsKey = "ModelField-DataModel-Shopify-AppSubscription-LineItems";
export const ShopifyGdprRequestTopicKey = "ModelField-DataModel-Shopify-GdprRequest-Topic";
export const ShopifyGdprRequestPayloadKey = "ModelField-DataModel-Shopify-GdprRequest-Payload";
export const ShopifyBulkOperationCreatedKey = "State-Shopify-Bulk-Operation-Created";
export const ShopifyBulkOperationCompletedKey = "State-Shopify-Bulk-Operation-Completed";
export const ShopifyBulkOperationCanceledKey = "State-Shopify-Bulk-Operation-Canceled";
export const ShopifyBulkOperationFailedKey = "State-Shopify-Bulk-Operation-Failed";
export const ShopifyBulkOperationExpiredKey = "State-Shopify-Bulk-Operation-Expired";

export const ShopifyBulkOperationCompleteKey = "Action-Shopify-Bulk-Operation-Complete";
export const ShopifyBulkOperationCompleteApiIdentifier = "complete";
export const ShopifyBulkOperationCancelKey = "Action-Shopify-Bulk-Operation-Cancel";
export const ShopifyBulkOperationCancelApiIdentifier = "cancel";
export const ShopifyBulkOperationFailKey = "Action-Shopify-Bulk-Operation-Fail";
export const ShopifyBulkOperationFailApiIdentifier = "fail";
export const ShopifyBulkOperationExpireKey = "Action-Shopify-Bulk-Operation-Expire";
export const ShopifyBulkOperationExpireApiIdentifier = "expire";
export const ShopifyBulkOperationUpdateKey = "Action-Shopify-Bulk-Operation-Update";
export const ShopifyBulkOperationUpdateApiIdentifier = "update";

const ShopifyBulkOperationActionKeyToApiIdentifierMap = {
  [ShopifyBulkOperationCompleteKey]: [ShopifyBulkOperationCompleteApiIdentifier],
  [ShopifyBulkOperationCancelKey]: [ShopifyBulkOperationCancelApiIdentifier],
  [ShopifyBulkOperationFailKey]: [ShopifyBulkOperationFailApiIdentifier],
  [ShopifyBulkOperationExpireKey]: [ShopifyBulkOperationExpireApiIdentifier],
  [ShopifyBulkOperationUpdateKey]: [ShopifyBulkOperationUpdateApiIdentifier],
} as const;

export const ShopifyActionKeysToApiIdentifiersMap: Record<string, readonly string[]> = {
  ...ShopifyShopActionKeyToApiIdentifiersMap,
  ...ShopifySyncActionKeyToApiIdentifierMap,
  ...ShopifyBulkOperationActionKeyToApiIdentifierMap,
} as const;

export const ShopifySyncSpecID = "shopify/sync";
export const ShopifyAbortSyncSpecID = "shopify/abort-sync";
export const ShopifyGlobalSyncSpecID = "shopify/global/sync";
export const ShopifySyncSyncSinceKey = "ModelField-DataModel-Shopify-Sync-syncSince";

export const ShopifyBulkOperationFinishSpecID = "shopify/bulk-operation/finish";
export const ShopifyTenancyVerifySpecID = "shopify/verify-tenancy";

export const ShopifyCartUniquenessValidationKey = "Validation-DataModel-ShopifyCart-Uniqueness";
export const ShopifyInventoryLevelUniquenessValidationKey = "Validation-DataModel-ShopifyInventoryLevel-Uniqueness";
export const ShopifyAssetKeyUniquenessValidationKey = "Validation-DataModel-ShopifyAsset-Key-Uniqueness";
export const ShopifyCustomerPaymentMethodUniquenessValidationKey = "Validation-DataModel-ShopifyCustomerPaymentMethod-Uniqueness";
export const ShopifyBusinessEntityUniquenessValidationKey = "Validation-DataModel-ShopifyBusinessEntity-Uniqueness";

/** Key for the special BelongsTo Shop key added to the session model when a connection is installed */
export const GadgetSessionToShopKey = "Model-Field-Belongs-To-Shop";

/** Key for the special BelongsTo Customer key added to the session model when a connection is installed with customer accounts extensions enabled */
export const GadgetSessionToCustomerKey = "Model-Field-Belongs-To-Customer";

/** Key for the special Shopify SID field added to the session model when a connection is installed */
export const GadgetSessionShopifySIDKey = "Model-Field-Shopify-SID";

export const ShopifyAppRoleKey = "Role-Shopify-App";
export const ShopifyAppRoleName = "shopify-app-users";

export const ShopifyCustomerRoleKey = "Role-Shopify-Customer";
export const ShopifyCustomerRoleName = "shopify-storefront-customers";

// Keys representing "core" models, these are the ones that we want to show to developers first

export const ShopifyCoreModelKeys = [ShopifyProductKey, ShopifyOrderKey, ShopifyCustomerKey, ShopifyCheckoutKey] as const;

// Keys that we allow to be accessed by the unauthenticated role

export const ShopifyUnauthenticatedAccessModelKeys = [
  ShopifyArticleKey,
  ShopifyAssetKey,
  ShopifyBlogKey,
  ShopifyCartKey,
  ShopifyCollectionKey,
  ShopifyCollectKey,
  ShopifyCommentKey,
  ShopifyFileKey,
  ShopifyPageKey,
  ShopifyProductImageKey,
  ShopifyProductMediaKey,
  ShopifyProductVariantMediaKey,
  ShopifyProductKey,
  ShopifyProductOptionKey,
  ShopifyProductVariantKey,
  ShopifyRedirectKey,
  ShopifySellingPlanGroupProductVariantKey,
  ShopifySellingPlanGroupProductKey,
  ShopifySellingPlanGroupKey,
  ShopifySellingPlanKey,
  ShopifyThemeKey,
] as const;

export const DisallowedDisabledWebhookModels = [ShopifyShopKey] as const;
